<template>
  <div class="text-left">
    <div class="d-flex justify-space-between">
      <label
        v-if="label"
        :for="id">{{ label }}</label>
    </div>
    <v-input
      v-show="!hidden"
      :id="`${id}-wrapper`"
      @click="onClick">
      <template #prepend>
        <slot name="prepend" />
      </template>
      <input
        :id="id"
        ref="input"
        v-qx-input="directive"
        :value="inputValue"
        :class="{ 'with-icon': slots.icon, 'with-prepend': slots.prepend, errorInput: isErrorEnabled && errorMessage }"
        class="w-100 form-control flex-fill"
        :type="type"
        :placeholder="placeholder"
        :tabindex="tabindex"
        :readonly="readonly"
        :autofocus="autofocus"
        :disabled="disabled"
        :maxlength="maxLength"
        :autocomplete="autocomplete"
        v-on="handlers"
        @blur="onBlur"
        @keydown="onKeydown"
        @keyup.caps-lock="isPassword && changeCapsLockEnabling(false)">
      <template #append>
        <slot name="icon" />
      </template>
    </v-input>
    <div class="after-input">
      <span
        v-if="capsLock.enabled"
        class="validator-text">
        {{ capsLock.text }}
      </span>
      <span
        v-if="isErrorEnabled"
        :id="errorId"
        class="validator-error">
        {{ errorMessage || validatorText }}
      </span>
    </div>
  </div>
</template>

<script setup>
/* Imports */

import { toRefs, computed, watch, reactive, useSlots } from 'vue';
import { qxInput as vQxInput } from '@/directives/input';
import { defineValidationField } from '@/utils/validation/validationModes.js';

import gettext from '@/utils/translationInjector.js';
const { $gettext } = gettext;

const emits = defineEmits(['update:modelValue', 'click']);

const properties = defineProps({
  mode: {
    type: String,
    default: 'validDirty',
    validator: (value) => ['aggressive', 'passive', 'lazy', 'eager', 'validDirty'].includes(value),
  },
  id: {
    type: String,
    required: true,
  },
  name: {
    type: String,
    required: true,
  },
  label: {
    type: String,
    default: '',
  },
  rules: {
    type: [Object, String],
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
  type: {
    type: String,
    default: 'text',
    validator: (value) => ['text', 'password', 'number', 'email'].includes(value),
  },
  modelValue: {
    type: [String, Number],
    default: '',
  },
  directive: {
    type: String,
    default: '',
  },
  tabindex: {
    type: String,
    default: '',
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  autofocus: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  autocomplete: {
    type: String,
    default: 'off',
  },
  trim: {
    type: Boolean,
    default: false,
  },
  hidden: {
    type: Boolean,
    default: false,
  },
  maxLength: {
    type: [Number, null],
    default: null,
  },
  validatorText: {
    type: String,
    default: null,
  },
  errorId: {
    type: String,
    default: null,
  },
  enableErrorBlock: {
    type: Boolean,
    default: true,
  },
  onFocus: {
    type: Function,
    default: () => {},
  },
  onBlur: {
    type: Function,
    default: () => {},
  },
  onInput: {
    type: Function,
    default: () => {},
  },
  onChange: {
    type: Function,
    default: () => {},
  },
});
const props = toRefs(properties);
const slots = useSlots();
/* Input events and validation handling */
const { handlers, inputValue, errorMessage, handleBlur, handleChange } = defineValidationField(props, onBlurInput);

watch(props.modelValue, (val) => {
  if (val !== inputValue.value) {
    handleChange(val);
  }
});
watch(inputValue, (val) => {
  emits('update:modelValue', val);
});

function onClick() {
  if (!props.disabled) {
    emits('click');
  }
}

function onBlurInput() {
  if (props.trim && inputValue.value) {
    inputValue.value = inputValue.value.trim();
  }
  changeCapsLockEnabling(false);
  handleBlur();
}

const isErrorEnabled = computed(
  () => (!props.validatorText.value || props.validatorText.value || errorMessage.length) && props.enableErrorBlock.value
);

/* Checks for CapsLock enabling (only for password) */
const isPassword = computed(() => props.type.value === 'password');

const capsLock = reactive({
  enabled: false,
  text: $gettext('CapsLock is enabled'),
});
function onKeydown(e) {
  if (isPassword.value && !capsLock.enabled && typeof e.getModifierState === 'function') {
    changeCapsLockEnabling(e.getModifierState('CapsLock'));
  }
}
function changeCapsLockEnabling(value) {
  capsLock.enabled = value;
}
</script>

<style lang="scss" scoped>
:deep {
  .v-input__control {
    width: 100%;
  }
  .v-input--horizontal {
    display: flex;
    align-items: stretch;
    width: 100%;
  }
  .v-input__details {
    display: none;
  }

  .v-input__prepend {
    margin-right: 0;
    margin-inline-start: 0;
  }
  .v-input__append {
    margin-left: 0;
    background: $white;
    margin-inline-start: 0;
    button {
      display: flex;
    }
  }
  .visibility-icon {
    border-left: 0 !important;
  }
}
input {
  font-size: 15px;
  line-height: 1.6;
  &.with-icon {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    margin-right: 0;
  }
  &.with-prepend {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    margin-right: 0;
  }
  &:disabled {
    background: $disabled-gray;
  }
}
::-ms-reveal {
  display: none;
}
</style>
